<script>
import STable from '@/components/Table/index'
import {
  loadTrainingList,
  loadTrainingExpired,
  createTraining,
  updateTraining,
  deleteTraining,
  exportTrainingList,
  loadPermissionList
} from '@/api/data'
import { getI18nLanguage } from '@/locales'
import moment from 'moment'

export default {
  name: 'Training',
  components: {
    STable
  },
  data () {
    return {
      locale: {
        emptyText: this.$t('table.emptyText')
      },
      col: {
        md: 12,
        lg: 8,
        xl: 6,
        xxl: 4
      },
      queryParam: {
        name: '',
        qTime: []
      },
      drivers: [],
      // 表头
      columns: [
        {
          title: 'ID',
          dataIndex: 'id',
          align: 'center',
          width: 80
        },
        {
          title: this.$t('training.field.name'),
          dataIndex: 'name',
          align: 'center',
          width: 180
        },
        {
          title: this.$t('training.field.date'),
          dataIndex: 'graduation_date',
          align: 'center',
          width: 180
        },
        {
          title: this.$t('training.field.action'),
          dataIndex: 'action',
          align: 'center',
          width: 140,
          scopedSlots: { customRender: 'action' }
        }
      ],
      // 加载数据方法 必须为 Promise 对象
      loadData: parameter => {
        const params = {
          pageNo: parameter.pageNo,
          pageSize: parameter.pageSize,
          name: this.queryParam.name
        }
        if (this.queryParam.qTime && this.queryParam.qTime.length === 2) {
          params.start_date = this.queryParam.qTime[0].format('YYYY-MM-DD')
          params.end_date = this.queryParam.qTime[1].format('YYYY-MM-DD')
        }
        return loadTrainingList(params).then(res => {
          return res.result
        })
      },
      modalVisible: false,
      modalLoading: false,
      editingRecord: null,
      expiredDrivers: [],
      form: this.$form.createForm(this)
    }
  },
  computed: {
    expiredText () {
      return this.expiredDrivers
        .map(d => d.name ? `${d.name}(${d.last_date})` : `ID:${d.user_id}(${d.last_date})`)
        .join('、')
    }
  },
  created () {
    this.loadDrivers()
    this.loadExpired()
  },
  methods: {
    getErrorMessage (msg) {
      if (typeof msg === 'string') {
        return msg
      }
      const lang = getI18nLanguage()
      const langKey = lang.replace('-', '').toLowerCase()
      if (langKey.includes('zh')) {
        return msg['zh'] || msg['zh-CN'] || msg['en'] || msg['en-US'] || ''
      } else if (langKey.includes('ja')) {
        return msg['ja'] || msg['ja-JP'] || msg['en'] || msg['en-US'] || ''
      } else {
        return msg['en'] || msg['en-US'] || msg['zh'] || ''
      }
    },
    loadDrivers () {
      loadPermissionList({
        type: 'all',
        pageNo: 1,
        pageSize: 1000
      }).then(res => {
        const data = (res.result && res.result.data) || []
        this.drivers = data.map(u => {
          const name = u.name || ''
          const account = u.account || ''
          return {
            value: u.user_id,
            label: account ? `${name}(${account})` : name
          }
        })
      })
    },
    loadExpired () {
      loadTrainingExpired({}).then(res => {
        if (res.code === 0) {
          this.expiredDrivers = (res.result || [])
        }
      }).catch(() => {})
    },
    handleSearch () {
      this.$refs.table.refresh(true)
    },
    handleReset () {
      this.queryParam.name = ''
      this.queryParam.qTime = []
      this.$refs.table.refresh(true)
    },
    openCreate () {
      this.editingRecord = null
      this.modalVisible = true
      this.$nextTick(() => {
        this.form.resetFields()
      })
    },
    openEdit (record) {
      this.editingRecord = record
      this.modalVisible = true
      this.$nextTick(() => {
        this.form.resetFields()
        this.form.setFieldsValue({
          user_id: record.user_id,
          graduation_date: moment(record.graduation_date)
        })
      })
    },
    handleSave () {
      const { form: { validateFields } } = this
      validateFields((err, values) => {
        if (err) {
          return
        }
        this.modalLoading = true
        const params = {
          user_id: values.user_id,
          graduation_date: values.graduation_date.format('YYYY-MM-DD')
        }
        const req = this.editingRecord
          ? updateTraining(Object.assign({ id: this.editingRecord.id }, params))
          : createTraining(params)
        req.then(res => {
          if (res.code === 0) {
            this.$message.success(this.$t('training.message.save.success'))
            this.modalVisible = false
            this.$refs.table.refresh(this.editingRecord ? false : true)
            this.loadExpired()
          } else {
            this.$message.error(this.getErrorMessage(res.msg) || this.$t('training.message.save.fail'))
          }
        }).catch(() => {
          this.$message.error(this.$t('training.message.save.fail'))
        }).finally(() => {
          this.modalLoading = false
        })
      })
    },
    handleDelete (record) {
      const self = this
      this.$confirm({
        title: self.$t('training.delete.confirm.title'),
        content: self.$t('training.delete.confirm.content'),
        okText: self.$t('training.action.delete'),
        cancelText: self.$t('training.action.cancel'),
        onOk () {
          return deleteTraining({ id: record.id }).then(res => {
            if (res.code === 0) {
              self.$message.success(self.$t('training.message.delete.success'))
              self.$refs.table.refresh(false)
              self.loadExpired()
            } else {
              self.$message.error(self.getErrorMessage(res.msg) || self.$t('training.message.delete.fail'))
            }
          }).catch(() => {
            self.$message.error(self.$t('training.message.delete.fail'))
          })
        }
      })
    },
    handleExport () {
      const params = {
        name: this.queryParam.name,
        lang: getI18nLanguage(),
        fileName: this.$t('training.export.filename') + '.xlsx'
      }
      if (this.queryParam.qTime && this.queryParam.qTime.length === 2) {
        params.start_date = this.queryParam.qTime[0].format('YYYY-MM-DD')
        params.end_date = this.queryParam.qTime[1].format('YYYY-MM-DD')
      }
      const hide = this.$message.loading(this.$t('training.export.loading'), 0)
      exportTrainingList(params).then(res => {
        this.saveResultAsExcel(res, params.fileName)
      }).catch(() => {
        this.$message.error(this.$t('training.export.fail'))
      }).finally(() => {
        hide()
      })
    }
  }
}
</script>

<template>
  <div class="training-page">
    <div class="filter-card">
      <div class="filter-card__header">
        <span class="filter-card__title">{{ $t('training.search.title') }}</span>
      </div>
      <div class="filter-card__body">
        <a-row :gutter="16">
          <a-col :lg="col.lg" :xxl="col.xxl" :xl="col.xl" :md="col.md">
            <div class="filter-item">
              <label class="filter-item__label">{{ $t('training.field.name') }}</label>
              <a-input class="filter-item__input" v-model="queryParam.name" allowClear :placeholder="$t('training.field.name')" @pressEnter="handleSearch" />
            </div>
          </a-col>
          <a-col :lg="col.lg" :xxl="col.xxl" :xl="col.xl" :md="col.md">
            <div class="filter-item">
              <label class="filter-item__label">{{ $t('training.field.date') }}</label>
              <a-range-picker class="filter-item__input" v-model="queryParam.qTime" style="width: 100%" />
            </div>
          </a-col>
          <a-col :lg="col.lg" :xxl="col.xxl" :xl="col.xl" :md="col.md">
            <div class="filter-item">
              <a-button type="primary" @click="handleSearch">{{ $t('training.action.search') }}</a-button>
              <a-button class="training-ml8" @click="handleReset">{{ $t('training.action.reset') }}</a-button>
            </div>
          </a-col>
        </a-row>
      </div>
    </div>

    <div class="table-card">
      <a-alert
        v-if="expiredDrivers.length > 0"
        class="training-alert"
        type="warning"
        show-icon>
        <template slot="message">
          <div>{{ $t('training.notice.expired') }}</div>
          <div class="training-alert__list">{{ expiredText }}</div>
        </template>
      </a-alert>
      <div class="table-card__toolbar">
        <a-button type="primary" icon="plus" @click="openCreate">{{ $t('training.action.create') }}</a-button>
        <a-button icon="download" @click="handleExport">{{ $t('training.action.export') }}</a-button>
      </div>
      <s-table
        ref="table"
        :columns="columns"
        :data="loadData"
        :locale="locale"
        size="small">
        <div slot="action" slot-scope="text, record">
          <a-button type="link" size="small" icon="edit" @click="openEdit(record)">{{ $t('training.action.edit') }}</a-button>
          <a-button type="link" size="small" icon="delete" class="training-delete" @click="handleDelete(record)">{{ $t('training.action.delete') }}</a-button>
        </div>
      </s-table>
    </div>

    <!-- 添加/编辑讲习 -->
    <a-modal
      v-model="modalVisible"
      :title="editingRecord ? $t('training.modal.edit.title') : $t('training.modal.create.title')"
      :confirm-loading="modalLoading"
      :mask-closable="false"
      :width="520"
      @ok="handleSave">
      <a-form :form="form" :label-col="{ span: 5 }" :wrapper-col="{ span: 18 }">
        <a-form-item :label="$t('training.field.driver')">
          <a-select
            v-decorator="['user_id', { rules: [{ required: true, message: $t('training.form.user_id.tip') }] }]"
            show-search
            option-filter-prop="children"
            :placeholder="$t('training.form.user_id.placeholder')">
            <a-select-option v-for="d in drivers" :key="d.value" :value="d.value">{{ d.label }}</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item :label="$t('training.field.date')">
          <a-date-picker
            v-decorator="['graduation_date', { rules: [{ required: true, message: $t('training.form.date.tip') }] }]"
            style="width: 100%"
            :placeholder="$t('training.form.date.placeholder')" />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<style scoped lang="less">
@text-primary: rgba(0, 0, 0, 0.85);
@text-secondary: rgba(0, 0, 0, 0.45);
@radius: 6px;
@shadow-card: 0 1px 2px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04);

.filter-card {
  background: #fff;
  border-radius: @radius;
  box-shadow: @shadow-card;
  overflow: hidden;

  &__header {
    padding: 12px 20px 0;
  }

  &__title {
    font-size: 15px;
    font-weight: 600;
    color: @text-primary;
  }

  &__body {
    padding: 12px 20px 16px;
  }
}

.filter-item {
  display: flex;
  align-items: center;

  &__label {
    flex-shrink: 0;
    font-size: 13px;
    color: @text-secondary;
    margin-right: 8px;
    white-space: nowrap;
    text-align: right;
  }

  &__input {
    flex: 1;
    min-width: 0;
  }
}

.table-card {
  margin-top: 12px;
  background: #fff;
  border-radius: @radius;
  box-shadow: @shadow-card;
  padding: 16px 20px;

  &__toolbar {
    display: flex;
    gap: 8px;
    margin-bottom: 12px;
  }
}

.training-alert {
  margin-bottom: 12px;

  &__list {
    margin-top: 4px;
    font-size: 13px;
  }
}

.training-ml8 {
  margin-left: 8px;
}

.training-delete {
  color: #f5222d;
}
</style>
